import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Replay } from '../replay';
import { SettingService } from '../../../services/setting.service';
import { EMAIL_PLACEHOLDERS } from '../../../settings/email-placeholders';
import { PlaceholderTarget } from '../../../settings/placeholder-insert.component';

/**
 * Canned Replies (mockup settings "Canned Replies"), the last section of Settings > Appointment
 * Emails & Canned Replies (embedded). The replies, newest first, with add and edit in a dialog -
 * the email body editor, "Insert into" placeholder tool and the placeholder list.
 * /canned-replies/:id (and the old /edit-canned-reply/:id) opens that page with this reply's
 * dialog. Same API as before (replies/*).
 */
@Component({
  selector: 'app-add-canned-reply',
  templateUrl: './add-canned-reply.component.html',
  styleUrls: ['./add-canned-reply.component.scss']
})
export class AddCannedReplyComponent implements OnInit {
  // Inside Appointment Emails & Canned Replies: no page heading of its own.
  @Input() embedded = false;
  readonly placeholders = EMAIL_PLACEHOLDERS;
  private targets: PlaceholderTarget[] = [];

  replies: any[] = [];
  loaded = false;
  message = '';
  error = '';

  editing: Replay = null;   // the dialog's reply; no id = new
  saving = false;
  formError = '';
  nameError = '';
  showPlaceholders = false;
  deleting: any = null;
  private openId: number = null;

  constructor(private settingService: SettingService, private route: ActivatedRoute, private router: Router) { }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.openId = params.id ? +params.id : null;
      this.openFromUrl();
    });
    this.load();
  }

  private load() {
    this.settingService.getReplies().subscribe(
      data => {
        this.replies = data || [];
        this.loaded = true;
        this.openFromUrl();
      },
      err => this.error = typeof err === 'string' ? err : 'The canned replies could not be loaded.'
    );
  }

  private openFromUrl() {
    if (!this.openId || !this.loaded) {
      return;
    }
    const reply = this.replies.filter(r => r.id == this.openId)[0];
    this.openId = null;
    if (reply) {
      this.edit(reply);
    } else {
      this.error = 'That canned reply no longer exists.';
    }
  }

  add() {
    this.open(new Replay(null, '', '', '', true, false));
  }

  edit(r: any) {
    this.open(new Replay(r.id, r.name, r.subject, r.reply, r.status == 1, r.header_footer_included == 1));
  }

  private open(reply: Replay) {
    this.editing = reply;
    this.formError = '';
    this.nameError = '';
    this.message = '';
  }

  close() {
    this.editing = null;
    // Leave /canned-replies/:id so a refresh does not reopen the dialog.
    if (this.route.snapshot.params.id) {
      this.router.navigate(['/auto-emails']);
    }
  }

  /** "Insert into": the body editor or the subject (the input exists while the dialog is open). */
  placeholderTargets(subject: HTMLInputElement): PlaceholderTarget[] {
    if (!this.targets.length || this.targets[1].input !== subject) {
      this.targets = [
        { label: 'Email Body', editor: true },
        { label: 'Subject', input: subject, set: v => this.editing.subject = v },
      ];
    }
    return this.targets;
  }

  save() {
    const r = this.editing;
    this.nameError = (r.name || '').trim() ? '' : 'Enter the reply name.';
    if (this.nameError) {
      return;
    }
    const request = r.id ? this.settingService.updateReply(r.id, r) : this.settingService.newReply(r);
    this.saving = true;
    this.formError = '';
    request.subscribe(
      () => {
        this.saving = false;
        this.message = r.id ? 'Canned reply updated.' : 'Canned reply added.';
        this.close();
        this.load();
      },
      err => {
        this.saving = false;
        this.formError = typeof err === 'string' ? err : 'The canned reply could not be saved.';
      }
    );
  }

  confirmDelete(r: any) {
    this.deleting = r;
    this.message = '';
  }

  remove() {
    this.saving = true;
    this.settingService.deleteReply(this.deleting.id).subscribe(
      () => {
        this.saving = false;
        this.deleting = null;
        this.message = 'Canned reply deleted.';
        this.load();
      },
      err => {
        this.saving = false;
        this.deleting = null;
        this.error = typeof err === 'string' ? err : 'The canned reply could not be deleted.';
      }
    );
  }
}
